<template>
  <div>
    <SmsIncidentBanner v-if="isSmsIncidentActive"/>
    <PartnerProgramBanner v-if="!isSmsIncidentActive && shouldShowPartnerBanner"/>
    <CookieConsentBanner/>
    <NuxtLayout>
      <NuxtPage/>
    </NuxtLayout>
    <ModalsContainer/>
    <Toast/>
  </div>
</template>

<script lang="ts" setup>
import {ModalsContainer, useModal} from "vue-final-modal";
import type {TUserProfile} from "~/types/user";
import {DOCTOR_WIDGET_PREFIX} from "~/stores/auth";

const FormCreatePassword = defineAsyncComponent(() => import('~/components/Modal/FormCreatePassword.vue'));
const FormAuthClinic = defineAsyncComponent(() => import('~/components/Modal/FormAuthClinic.vue'));
const FormAuthPhone = defineAsyncComponent(() => import('~/components/Modal/FormAuthPhone.vue'));

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const {isAuth} = storeToRefs(authStore);
const {apiQuery} = useApi();
const {$pwa} = useNuxtApp();
const resetPasswordData = ref<{ token: string, email: string }>({token: '', email: ''});
const {hasPartnerPromoEntry, isPartnerPromoPage} = usePartnerPromoEntry();
const hasPartnerPromoVisit = ref(false);
const {isSmsIncidentActive, fetchSmsIncident, watchSmsIncident} = useSmsIncident();
watchSmsIncident();
const isBlogPage = computed(() => route.path === '/blog/' || route.path === '/blog' || route.path.startsWith('/blog/'));
const shouldShowPartnerBanner = computed(() => {
  if (route.path.startsWith(DOCTOR_WIDGET_PREFIX)) {
    return false;
  }

  if (isBlogPage.value) {
    return true;
  }

  if (isPartnerPromoPage(route.path)) {
    return true;
  }

  return hasPartnerPromoVisit.value;
});

const {isShopHost, canonicalOrigin: shopCanonicalOrigin} = useShopHost();
const canonicalOrigin = isShopHost.value ? shopCanonicalOrigin : 'https://vetsy.ru';
const canonicalLink = computed(() => {
  let path = route.path;
  if (!path.endsWith('/')) {
    path += '/';
  }
  return canonicalOrigin + path;
})
useColorThemeHead();

useHead({
  link: [
    {
      rel: 'canonical',
      href: () => canonicalLink.value,
    },
  ],
  meta: [
    {property: 'og:url', content: () => canonicalLink.value},
    {property: 'og:type', content: 'website'},
    {property: 'og:image', content: 'https://vetsy.ru/images/og-default.jpg'},
    {property: 'og:image:width', content: '1024'},
    {property: 'og:image:height', content: '546'},
    {property: 'og:site_name', content: 'Vetsy.ru'},
    {name: 'yandex-verification', content: 'a46f9bd74dd20184'},
    {name: 'yandex-verification', content: '26601fe884d6f59c'},
  ],
});

onMounted(() => {
  hasPartnerPromoVisit.value = hasPartnerPromoEntry();
  fetchSmsIncident();

  if (route.query?.token && route.query?.email) {
    resetPasswordData.value = {token: route.query?.token as string, email: route.query?.email as string};
    router.replace({query: {}});
    showCreatePassword();
  }

  if ('auth_form' in route.query && !isAuth.value) {
    showAuthModal();
  }
})

watch(() => route.fullPath, () => {
  hasPartnerPromoVisit.value = hasPartnerPromoEntry();
  fetchSmsIncident();
});

if ($pwa && $pwa.isPWAInstalled) {
  onBeforeMount(() => {
    document.body.classList.add('pwa');
  })
}

const getUserProfile = async () => {
  await apiQuery<{ data: TUserProfile }>('/client/profile/', 'GET', {})
      .then((data) => {
        authStore.setUserProfileData(data.data);
      })
}

const showCreatePassword = () => {
  const {open, close} = useModal({
    component: FormCreatePassword,
    attrs: {
      token: resetPasswordData.value.token,
      email: resetPasswordData.value.email,
      onClose(success?: boolean) {
        close();
        if (success) {
          setTimeout(() => {
            showClinicAuth();
          }, 100)
        }
      },
    }
  });
  open();
}

const {open: showClinicAuth, close: closeClinicAuth} = useModal({
  component: FormAuthClinic,
  attrs: {
    onClose(success?: boolean) {
      closeClinicAuth()
      if (success) {
        router.push({path: '/profile-clinic/'})
      }
    },
  }
});
const {open: showVetAuth, close: closeVetAuth} = useModal({
  component: FormAuthPhone,
  attrs: {
    isDoctor: true,
    onClose(success?: boolean) {
      closeVetAuth()
      if (success) {
        router.push({path: '/profile-doctor/'})
      }
    },
    onSwitchRole(role: 'doctor' | 'clinic' | 'client') {
      closeVetAuth();
      if (role === 'client') {
        setTimeout(() => showAuthModal(), 100);
      }
    },
  }
});
const {open: showAuthModal, close: closeAuthModal} = useModal({
  component: FormAuthPhone,
  attrs: {
    async onClose(success?: boolean) {
      closeAuthModal()
      if (success) {
        await getUserProfile();
        router.push({path: '/profile/'})
      }
    },
    onSwitchRole(role: 'doctor' | 'clinic') {
      closeAuthModal();
      // vue-final-modal не поднимает новый инстанс в том же тике, где закрывается
      // предыдущий — тот же приём, что и в showCreatePassword выше.
      setTimeout(() => (role === 'doctor' ? showVetAuth() : showClinicAuth()), 100);
    },
  }
});
</script>

<style lang="scss">
.site-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  &__main {
    flex: 1 1 auto;
  }
}

.pwa {
  #carrotquest-messenger-collapsed-container {
    display: none !important;
  }
}
</style> 
